/**
 * Silent refresh with cross-tab lock (avoids refresh-token rotation races).
 */

import {
  persistTokens,
  getRefreshToken,
  getTokensUpdatedAt,
} from './tokenStorage';
import { publishSession } from './authSync';
import { getOrCreateClientDeviceId } from './deviceId';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
const LOCK_KEY = 'smartgaz_refresh_lock';
const LOCK_TTL_MS = 12_000;
const WAIT_POLL_MS = 120;
const WAIT_MAX_MS = 10_000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function readLock() {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function acquireLock() {
  const now = Date.now();
  try {
    localStorage.setItem(LOCK_KEY, String(now));
  } catch {
    // ignore
  }
  return now;
}

function releaseLock(ourTs) {
  try {
    if (readLock() === ourTs) localStorage.removeItem(LOCK_KEY);
  } catch {
    // ignore
  }
}

async function waitForOtherTabRefresh(beforeTs) {
  const deadline = Date.now() + WAIT_MAX_MS;
  while (Date.now() < deadline) {
    const lock = readLock();
    const updatedAt = getTokensUpdatedAt();
    if (updatedAt > beforeTs) return true;
    if (!lock || Date.now() - lock > LOCK_TTL_MS) return false;
    await sleep(WAIT_POLL_MS);
  }
  return getTokensUpdatedAt() > beforeTs;
}

async function fetchRefresh({ useBodyToken } = {}) {
  const refreshToken = useBodyToken ? getRefreshToken() : null;
  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Device-Id': getOrCreateClientDeviceId(),
    },
    credentials: 'include',
    body: JSON.stringify(refreshToken ? { refreshToken } : {}),
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

/**
 * @returns {Promise<{ accessToken: string, refreshToken?: string }>}
 */
export async function silentRefresh() {
  const beforeTs = getTokensUpdatedAt();
  const existingLock = readLock();

  if (existingLock && Date.now() - existingLock < LOCK_TTL_MS) {
    const ok = await waitForOtherTabRefresh(beforeTs);
    if (ok) {
      return { accessToken: null, reused: true };
    }
  }

  const ourLock = acquireLock();
  try {
    // First try: cookie-based refresh (no body token)
    let { res, data } = await fetchRefresh({ useBodyToken: false });

    // If cookie failed and we have a stored token, retry with it in the body
    if ((!res.ok || data.status !== 'SUCCESS') && getRefreshToken()) {
      ({ res, data } = await fetchRefresh({ useBodyToken: true }));
    }

    if (!res.ok || data.status !== 'SUCCESS' || !data.accessToken) {
      const err = new Error(data.reason || 'REFRESH_FAILED');
      err.code = data.reason;
      throw err;
    }

    persistTokens(data.accessToken, data.refreshToken);
    publishSession({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      clientDeviceId: getOrCreateClientDeviceId(),
    });

    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    };
  } finally {
    releaseLock(ourLock);
  }
}
